import { ChartDayOffsets, generateChartData } from "./helpers";
import { Chart as ChartJS, registerables } from "chart.js";
import type { ChartOptions, ChartType, Plugin } from "chart.js";

import { Chart } from "react-chartjs-2";
import { ContextMenu } from "shared/UI/ContextMenu/ContextMenu";
import { Hideable } from "shared/UI/Hideable/Hideable";
import { RoundedCard } from "shared/UI/RoundedCard/RoundedCard";
import Button from "shared/UI/Buttons/Button";
import { UserTrackerValue } from "shared/request/myHealthyAdvantageApi";
import { ValueCallback } from "shared/core/types/callbacks";
import { ViewStateWrapper } from "UIPalette/ViewStateWrapper/ViewStateWrapper";
import theme from "../../../../../../../../theme";
import { trackChartFilter } from "core/monitoring/Events";
import { useState } from "react";
import { useTranslation } from "react-i18next";

ChartJS.register(...registerables);

export interface TrackerChartProps<TType extends ChartType = ChartType> {
  heading: string;
  yAxisStepSize: number;
  loading: boolean;
  showError: boolean;
  data: UserTrackerValue[];
  reload: VoidFunction;
  dateRange: { startDate: Date; endDate: Date };
  onChartViewOffsetChange: ValueCallback<ChartDayOffsets>;
  initialChartViewOffset: ChartDayOffsets;
  showYAxisTick?: boolean;
  yAxisMax?: number;
  yAxisLabel?: string;
  plugins?: Plugin<TType>[];
  menuItems?: React.ReactNode[];
  trackerType?: string;
}

export const TrackerChart = ({
  heading,
  data,
  dateRange,
  showError,
  reload,
  loading,
  onChartViewOffsetChange,
  initialChartViewOffset,
  showYAxisTick = true,
  yAxisStepSize,
  yAxisLabel,
  yAxisMax,
  plugins,
  menuItems,
  trackerType,
}: TrackerChartProps) => {
  const { t } = useTranslation();

  const [view, setView] = useState(initialChartViewOffset);

  const setChartView = (newView: ChartDayOffsets) => {
    trackerType && trackChartFilter(trackerType);
    setView(newView);
    onChartViewOffsetChange(newView);
  };

  return (
    <RoundedCard>
      <h2 className="flex flex-row items-center">
        <span className="flex-grow">{heading}</span>
        <Hideable hidden={!menuItems || showError || loading}>
          <ContextMenu
            menuPosition="left"
            aria-label={t("trackers.actionsMenu")}
          >
            {menuItems}
          </ContextMenu>
        </Hideable>
      </h2>
      <ViewStateWrapper loading={loading} error={showError} errorMessage={t("wellbeing.trackers.chart.error")} onRetry={reload}>
        <div className="flex flex-col gap-4 h-82">
          <div className="h-72">
            <Chart
              type="bar"
              options={getChartOptions(yAxisStepSize, showYAxisTick, yAxisLabel, yAxisMax)}
              data={generateChartData(data, dateRange, view)}
              plugins={plugins}
            />
          </div>
          <div className="flex flex-row items-center w-full space-x-3 justify-evenly">
            <Button
              variant={view !== ChartDayOffsets.week ? "outline" : "default"}
              text={t("wellbeing.chartButtons.view7Days")}
              onClick={() => setChartView(ChartDayOffsets.week)}
            />
            <Button
              variant={view !== ChartDayOffsets.month ? "outline" : "default"}
              text={t("wellbeing.chartButtons.view1Month")}
              onClick={() => setChartView(ChartDayOffsets.month)}
            />
            <Button
              variant={view !== ChartDayOffsets.year ? "outline" : "default"}
              text={t("wellbeing.chartButtons.view1Year")}
              onClick={() => setChartView(ChartDayOffsets.year)}
            />
          </div>
        </div>
      </ViewStateWrapper>
    </RoundedCard>
  );
};

function getChartOptions(yAxisStepSize: number, showYAxisTick: boolean, yAxisLabel?: string, yAxisMax?: number): ChartOptions {
  const chartFontFamily = "'Quicksand', sans-serif";
  const chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        type: "linear" as const,
        display: true,
        min: 0,
        max: yAxisMax,
        suggestedMax: yAxisStepSize,
        grid: {
          drawOnChartArea: false,
        },
        title: {
          display: true,
          text: yAxisLabel,
          padding: yAxisLabel ? 0 : 8,
          color: theme.colours["font-dark"],
          font: {
            family: chartFontFamily,
          },
        },
        ticks: {
          color: theme.colours["font-dark"],
          font: {
            family: chartFontFamily,
          },
          stepSize: yAxisStepSize,
          callback: function (label) {
            if (showYAxisTick) {
              const parsedLabel = parseInt(label.toString());
              return new Intl.NumberFormat().format(parsedLabel);
            }
            return "";
          },
        },
      },
      x: {
        display: true,
        grid: {
          drawOnChartArea: true,
        },
        ticks: {
          color: theme.colours["font-dark"],
          maxRotation: 57.899,
          minRotation: 57.899,
          font: {
            family: chartFontFamily,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  return chartOptions;
}
